import {
  AlertRuleRecipients,
  AlertRuleGroupRecipients,
} from "api/interfaces/alertInterface.interface";

type RecipientType = AlertRuleRecipients | AlertRuleGroupRecipients;

// 헤더 체크박스 상태 계산 공통 함수
export const getHeaderCheckboxState = <T extends RecipientType>({
  selectedEnable,
  field,
}: {
  selectedEnable: T[];
  field: "enableEmail" | "enablePush";
}) => {
  const values = selectedEnable.map((g) => g[field]);
  const checked = selectedEnable.length > 0 && values.every((gc) => gc);
  const indeterminate = selectedEnable.length > 0 && values.some((gc) => gc);
  return { checked, indeterminate: !checked ? indeterminate : false };
};

// 개별 체크박스 토글 로직 공통 함수
export const createCellToggleHandler = <
  T extends AlertRuleRecipients | AlertRuleGroupRecipients
>({
  selectedEnable,
  onCheckRecipient,
  getId,
}: {
  selectedEnable: T[];
  onCheckRecipient: (recipients: T[]) => void;
  getId: (item: T) => string;
}): (params: { field: "enableEmail" | "enablePush"; id: string }) => void => {
  return ({
    field,
    id,
  }: {
    field: "enableEmail" | "enablePush";
    id: string;
  }) => {
    const currentItem = selectedEnable.find((item) => getId(item) === id);
    if (!currentItem) return;

    const currentEmailValue = currentItem.enableEmail || false;
    const currentPushValue = currentItem.enablePush || false;

    onCheckRecipient([
      {
        ...currentItem,
        enableEmail:
          field === "enableEmail" ? !currentEmailValue : currentEmailValue,
        enablePush:
          field === "enablePush" ? !currentPushValue : currentPushValue,
      } as T,
    ]);
  };
};

// 헤더 토글 로직 공통 함수
export const createHeaderToggleHandler = <
  TData extends { [key: string]: any },
  TRecipient extends AlertRuleRecipients | AlertRuleGroupRecipients
>({
  filteredData,
  allItems,
  existingRecipients,
  onCheckRecipient,
  getId,
  getRecipientId,
  createRecipient,
}: {
  filteredData: TData[];
  allItems: TData[];
  existingRecipients: TRecipient[] | undefined;
  onCheckRecipient: (recipients: TRecipient[]) => void;
  getId: (item: TData) => string;
  getRecipientId: (recipient: TRecipient) => string;
  createRecipient: (
    id: string,
    enableEmail: boolean,
    enablePush: boolean
  ) => TRecipient;
}): (params: {
  field: "enableEmail" | "enablePush";
  checked: boolean;
}) => void => {
  return ({
    field,
    checked,
  }: {
    field: "enableEmail" | "enablePush";
    checked: boolean;
  }) => {
    // 현재 필터링된 data의 id들만 업데이트
    const filteredIds = filteredData.map(getId);
    // 전체 items에서 필터링된 항목들만 업데이트
    const updatedRecipients = allItems
      .filter((item) => filteredIds.includes(getId(item)))
      .map((item) => {
        const itemId = getId(item);
        // 기존 값을 가져오기 (우선순위: existingRecipients > allItems 상태)
        const existingRecipient = existingRecipients?.find(
          (r) => getRecipientId(r) === itemId
        );
        const currentItem = allItems.find((i) => getId(i) === itemId);

        // 각 필드의 값을 결정하는 헬퍼 함수
        const getFieldValue = (
          fieldName: "enableEmail" | "enablePush"
        ): boolean => {
          if (field === fieldName) {
            return checked;
          }
          return (
            existingRecipient?.[fieldName] ??
            (currentItem as any)?.[fieldName] ??
            false
          );
        };

        return createRecipient(
          itemId,
          getFieldValue("enableEmail"),
          getFieldValue("enablePush")
        );
      });
    onCheckRecipient(updatedRecipients);
  };
};
